import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { menuInfo } from "@shared/type/CommonType";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";
import { global } from "@shared/libs/Global";
import SnSwitch, { SnSwitchHandle } from "@shared/components/SnSwitch";
import {
  applyAutoSlip,
  fnGetIotype,
  getInitFormData,
} from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import { dateToString } from "@shared/utils/DateUtils";
import { RowNode } from "ag-grid-community";
import { showResizablePopupModal } from "@widgets/CommonModal";
import {
  fnGetWhLocLotCtPltInv,
  fnGetWhSnInv,
  fnGetWhLocPjtLotCtPltInv,
  fnGetWhPjtSnInv,
} from "@shared/utils/InvUtils";
import * as converter from "@shared/utils/ValueConverterUtils";
import SN_MTL_SCT_REG_003_POP_001 from "@menu/MTL/SN_MTL_SCT_REG_003/SN_MTL_SCT_REG_003_POP_001";
import SearchPanel from "@widgets/SearchPanel";
import SnResizeBar from "@shared/components/SnResizeBar";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { SnButton } from "@shared/components/SnButton";
import SnBarcodeBox from "@shared/components/SnBarcodeBox";
import SnNumberBox from "@shared/components/SnNumberBox";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";

interface Props {
  Menuprops: menuInfo;
}

// 외주출고처리(통합)
const SN_MTL_SCT_REG_003 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo, config } = global();
  const iotypeRef = useRef<any[]>([]);

  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
    grid1: React.createRef<SnGridRef>(),
    grid2: React.createRef<SnGridRef>(),
    grid3: React.createRef<SnGridRef>(),
    grid4: React.createRef<SnGridRef>(),
  });
  const txtBarcodeRef = useRef<any>(null);
  const numberBoxFifoRef = useRef<any>(null);
  const lotSnDataRef = useRef<Record<string, any>[]>([]);
  const switchRef = useRef<SnSwitchHandle>(null); // 스위치

  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
    grid4: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid4: [],
  });

  const [lotSnData, setLotSnData] = useState<Record<string, any>[]>([]); // 재고리스트
  const [selectedDocu, setSelectedDocu] = useState<string>("");

  // MAS0002(프로젝트 사용유무). 프로젝트가 헤더가 아니라 라인(grid2)에 있어
  // 재고 조회·캐시·필터를 라인의 cd_pjt 기준으로 나눈다. (MOW_006 과 동일)
  const mas0002 = config?.MAS0002?.dc_ctrl ?? "N"; // 프로젝트 사용유무 Y/N

  //#region 프로젝트 재고 헬퍼
  // 재고 캐시(lotSnData)·grid4 매칭 키. 프로젝트 사용 시 cd_pjt 까지 같아야 같은 재고로 본다.
  const sameInvKey = (a: any, b: any) =>
    a?.cd_item === b?.cd_item &&
    a?.cd_wh === b?.cd_wh &&
    (mas0002 !== "Y" || (a?.cd_pjt ?? "") === (b?.cd_pjt ?? ""));

  // grid3 필터 조건에 붙일 프로젝트 조건
  const pjtFilter = (cd_pjt: any) =>
    mas0002 === "Y" ? { cd_pjt: cd_pjt ?? "" } : {};

  // 프로젝트 재고 API 는 요청 최상위에 cd_pjt 하나만 받으므로 프로젝트별로 나눠 호출한다.
  const callByPjt = async (list: any[], api: (p: any) => Promise<any>) => {
    const groups = new Map<string, any[]>();
    list.forEach((it) => {
      const key = it.cd_pjt ?? "";
      groups.set(key, [...(groups.get(key) ?? []), { ...it, cd_pjt: key }]);
    });
    const results = await Promise.all(
      Array.from(groups.entries()).map(async ([key, group]) => {
        const rows: any[] = (await api(group)) ?? [];
        return rows.map((r) => ({ ...r, cd_pjt: r.cd_pjt ?? key }));
      })
    );
    return results.flat();
  };

  // 프로젝트 사용 시 프로젝트 재고, 아니면 기존 재고 API
  const fetchLotInv = async (params: any) => {
    const list = Array.isArray(params) ? params : [params];
    if (mas0002 !== "Y") return fnGetWhLocLotCtPltInv(list);
    return callByPjt(list, fnGetWhLocPjtLotCtPltInv);
  };
  const fetchSnInv = async (params: any) => {
    const list = Array.isArray(params) ? params : [params];
    if (mas0002 !== "Y") return fnGetWhSnInv(list);
    return callByPjt(list, fnGetWhPjtSnInv);
  };
  //#endregion

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData);
        setFormData((prev: any) => ({
          ...(rtn.formData || {}),
          ...(prev || {}),
        }));
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
          grid3: rtn.grid.grid3,
          grid4: rtn.grid.grid4,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useEffect(() => {
    if (
      Menuprops.figure &&
      columns.grid1.length > 0 &&
      columns.grid2.length > 0
    ) {
      onActivate(Menuprops.nodeData);
    }
  }, [columns, Menuprops]);

  useEffect(() => {
    lotSnDataRef.current = lotSnData;
  }, [lotSnData]);

  useEffect(() => {
    const fetchData = async () => {
      const data = await fnGetIotype("MTL", "2", "5", "");
      iotypeRef.current = data;
    };
    fetchData();
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  const onSearch = async (searchData: any) => {
    const modifiedRowsH = getGridCurrent("grid1").getChange();
    const modifiedRowsL = getGridCurrent("grid2").getChange();
    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirmCancel",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) return;
      } else if (confirm === undefined) {
        return;
      }
    }

    try {
      const searchDataToUse = searchData || searchRef.current.getFormdata();
      const params = {
        id: "sn_mtl_sct_reg_003_01",
        params: searchDataToUse,
      };

      setLotSnData([]);

      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid4").resetUpdate();
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
        grid2: data[1] || [],
        grid3: [],
        grid4: data[2] || [],
      });
    } catch (error: any) {}
  };

  const onInsert = () => {
    try {
      const addedNode = getGridCurrent("grid1").addRow();
      addedNode?.setDataValue("cd_io", addedNode.data?.cd_io);
    } catch (error: any) {}
  };

  const onBeforeAddRow1 = (): any => {
    try {
      const modifiedRowsH = getGridCurrent("grid1").getChange();
      if (modifiedRowsH.modifiedRowcount > 0) {
        showDialog("alter", "수정된 내용이 있습니다.");
        return false;
      }

      const searchInfo = searchRef.current.getFormdata();
      const today = dateToString(new Date());
      return {
        cd_firm: userInfo?.cd_firm,
        cd_biz: searchInfo?.cd_biz,
        dt_io: today,
        cd_emp: userInfo?.cd_emp,
        nm_emp: userInfo?.nm_emp,
        cd_dept: userInfo?.cd_dept,
        nm_dept: userInfo?.nm_dept,
        cd_gp: userInfo?.cd_gp,
        cd_cust: userInfo?.cd_cust,
        nm_cust_sort: userInfo?.nm_cust_sort,
        cd_io: "top1",
        fg_trns: "1",
        cd_exch: "000",
        rt_exch: 1,
        fg_slip: "2",
        tp_slip: "5",
      };
    } catch (error: any) {
      return false;
    }
  };

  const onAddRow = () => {
    try {
      return getGridCurrent("grid2").addRow();
    } catch (error: any) {}
  };

  const onBeforeAddRow2 = (): any => {
    try {
      getGridCurrent("grid1").stopEditing();
      const focusedNodeH = getGridCurrent("grid1").focusedRowNode();
      if (focusedNodeH == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      const requiredValuesCheckH =
        getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }

      const today = dateToString(new Date());
      const maxNoLine = getGridCurrent("grid2").getMaxValue("no_line");
      return {
        cd_firm: focusedNodeH.data?.cd_firm,
        cd_biz: focusedNodeH.data?.cd_biz,
        no_io: focusedNodeH.data?.no_io,
        cd_exch: focusedNodeH.data?.cd_exch,
        no_line: maxNoLine + 1,
        dt_io: focusedNodeH.data?.dt_io,
        fg_slip: focusedNodeH.data?.fg_slip,
        tp_slip: focusedNodeH.data?.tp_slip,
        cd_io: focusedNodeH.data?.cd_io,
        fg_io: focusedNodeH.data?.fg_io,
        fg_money: focusedNodeH.data?.fg_money,
        fg_map: focusedNodeH.data?.fg_map,
        yn_req: "N",
        rt_exch: focusedNodeH.data?.rt_exch,
        fg_vat: "11",
        cd_wh: focusedNodeH.data?.cd_wh,
        cd_wh_ref: focusedNodeH.data?.cd_wh_ref,
        qt: 0,
        qt_req: 0,
        qt_good: 0,
        qt_bad: 0,
      };
    } catch (error: any) {
      return false;
    }
  };

  const onDelete = async () => {
    if (getGridCurrent("grid1").getDisplayedRowCount() < 1) return;

    const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
    if (!focusedRowNodeH) {
      await showDialog("alter", "선택된 행이 없습니다.");
      return false;
    }

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");
    if (!confirm) return;

    // 삭제할 헤더의 no_io로 출고정보를 필터한다.
    getGridCurrent("grid4").applyFilters({
      no_io: focusedRowNodeH.data.no_io,
    });

    getGridCurrent("grid4").deleteAllRows();
    getGridCurrent("grid2").deleteAllRows();
    getGridCurrent("grid1").deleteSelectedRows();

    const realDeletedRows = getGridCurrent("grid1").getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid3").resetUpdate();
      return;
    }

    return await onSave();
  };

  const onDeleteRow = () => {
    if (getGridCurrent("grid2").getDisplayedRowCount() < 1) return;

    const focusedRowNode = getGridCurrent("grid2").focusedRowNode();

    const insertRows = getGridCurrent("grid2").getChange().insert ?? [];
    const isInsertRow = insertRows.some(
      (row: any) =>
        row.no_line === focusedRowNode.data.no_line &&
        row.no_io === focusedRowNode.data.no_io
    );

    if (isInsertRow) {
      resetRowData();
    } else {
      getGridCurrent("grid4").deleteAllRows();
    }

    getGridCurrent("grid2").deleteSelectedRows();

    if (getGridCurrent("grid2").getDisplayedRowCount() < 1) {
      getGridCurrent("grid3").applyFilters({
        cd_item: "",
        cd_wh: "",
        ...pjtFilter(""),
      });
    }
  };

  const onSave = async () => {
    try {
      getGridCurrent("grid1").stopEditing();
      getGridCurrent("grid2").stopEditing();

      const requiredValuesCheckH =
        getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheckH) return false;

      const requiredValuesCheckL =
        getGridCurrent("grid2").requiredValuesCheck();
      if (!requiredValuesCheckL) return false;

      const qtiohChanges = getGridCurrent("grid1").getChange();
      const qtioChanges = getGridCurrent("grid2").getChange();
      const qtiolotsnChanges = getGridCurrent("grid4").getChange();

      const headerInserts = qtiohChanges.insert ?? [];
      const lineInserts = qtioChanges.insert ?? [];
      if (headerInserts.length > 0 && lineInserts.length === 0) {
        await showDialog(
          "alter",
          "상세 정보가 없습니다.\n행추가하여 상세정보 작성 후 저장해 주시기 바랍니다."
        );
        return false;
      }

      if (
        qtiohChanges.modifiedRowcount === 0 &&
        qtioChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      if (headerInserts.length > 0) {
        if (headerInserts[0].cd_wh == headerInserts[0].cd_wh_ref) {
          const confirm = await showDialog(
            "confirm",
            "출고창고와 입고창고가 동일합니다. 계속하시겠습니까?)"
          );
          if (!confirm) return false;
        }
      }

      for (const l of lineInserts) {
        if (l.qt === 0) {
          await showDialog(
            "alter",
            "출고 수량이 입력되지 않은 자재가 존재합니다."
          );
          return false;
        }
      }

      const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
      const noSlip = await applyAutoSlip({
        cd_column: "no_io",
        cd_module: "MTL",
        no_line: 18,
        dt: focusedRowNodeH.data.dt_io,
        girdrefs: [
          gridRefs.current["grid1"],
          gridRefs.current["grid2"],
          gridRefs.current["grid4"],
        ],
        inserts: [qtiohChanges, qtioChanges, qtiolotsnChanges],
        cd_menu: Menuprops.cd_menu,
      });

      const SaveData = {
        qtioh: qtiohChanges,
        qtio: qtioChanges,
        qtio_lotsn: qtiolotsnChanges,
      };

      const ok = await fnSave("SN_MTL_SCT_REG_003_SAVE_01", SaveData);
      if (!ok) {
        getGridCurrent("grid1").resetUpdate(true);
        getGridCurrent("grid2").resetUpdate(true);
        getGridCurrent("grid4").resetUpdate(true);
        return false;
      }

      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid4").resetUpdate();
      setLotSnData([]);
      return true;
    } catch (error: any) {
      getGridCurrent("grid1").resetUpdate(true);
      getGridCurrent("grid2").resetUpdate(true);
      getGridCurrent("grid4").resetUpdate(true);
      return false;
    }
  };

  const onGrid1BtnsClick = async (id: string) => {
    const qtiohChanges = getGridCurrent("grid1").getChange().insert;
    if (qtiohChanges.length === 0) {
      await showDialog("alter", "신규행을 추가하시기 바랍니다.");
      return;
    }

    switch (id) {
      case "btn_POP1":
        const focusedNodeH = getGridCurrent("grid1").focusedRowNode();
        const selectedFormData = searchRef.current.getFormdata();
        const selectedData = {
          cd_biz: selectedFormData.cd_biz,
          cd_cust: focusedNodeH.data.cd_cust || selectedFormData.cd_cust,
          nm_cust_sort:
            focusedNodeH.data.nm_cust_sort || selectedFormData.nm_cust_sort,
          // dt_from: selectedFormData.dt_from,
          // dt_to: selectedFormData.dt_to,
        };

        let focusedRowHIdx = 0;
        getGridCurrent("grid1").forEachFilteredNode((node: RowNode) => {
          if (node.data.no_io === "" || node.data.no_io === undefined) {
            focusedRowHIdx = node.rowIndex ?? focusedRowHIdx;
          }
        });

        getGridCurrent("grid1").selectAndFocusRow(focusedRowHIdx);

        const component = (
          <SN_MTL_SCT_REG_003_POP_001 selectedData={selectedData} />
        );
        const result = await showResizablePopupModal({
          title: "요청적용",
          children: component,
          validation: true,
        });
        if (!result || result === "close") return;

        const headerResult = result[0];
        const lineResult = result[1];

        const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
        if (focusedRowNodeH && headerResult[0]) {
          for (const key in headerResult[0]) {
            focusedRowNodeH.setDataValue(key, headerResult[0][key], "noEvent");
          }
          focusedRowNodeH.setDataValue(
            "cd_wh_ref",
            headerResult[0].cd_cust,
            "noEvent"
          );
        }

        const lotItemList: any = [];
        const snItemList: any = [];

        lineResult.map((result: any) => {
          if (headerResult[0]) {
            const addedNode = onAddRow();
            if (addedNode) {
              for (const key in result) {
                if (key != "no_line")
                  addedNode.setDataValue(key, result[key], "noEvent");
              }
              addedNode.setDataValue("yn_req", "Y", "noEvent");
              addedNode.setDataValue("cd_wh", headerResult[0].cd_wh, "noEvent");
              addedNode.setDataValue(
                "cd_wh_ref",
                headerResult[0].cd_cust,
                "noEvent"
              );
              if (result.fg_unit == "QT") {
                addedNode.setDataValue("qt_good", result.qt_app, "noEvent");
                addedNode.setDataValue("qt", result.qt_app, "noEvent");
              } else {
                addedNode.setDataValue("qt_good", 0, "noEvent");
                addedNode.setDataValue("qt", 0, "noEvent");
              }
              addedNode.setDataValue("qt_req", result.qt_app, "noEvent");

              // 프로젝트 사용(MAS0002=Y) 시 라인에 프로젝트가 없으면 재고를 불러오지 않는다
              const hasPjt =
                mas0002 !== "Y" || converter.getString(result.cd_pjt) !== "";
              if (hasPjt) {
                switch (result.fg_unit) {
                  case "LOT":
                    lotItemList.push({
                      cd_biz: focusedRowNodeH.data.cd_biz,
                      cd_item: result.cd_item,
                      cd_wh: headerResult[0].cd_wh,
                      cd_loc: "",
                      cd_pjt: result.cd_pjt ?? "", // 요청 라인의 프로젝트
                    });
                    break;
                  case "SN":
                    snItemList.push({
                      cd_biz: focusedRowNodeH.data.cd_biz,
                      cd_item: result.cd_item,
                      cd_wh: headerResult[0].cd_wh,
                      cd_pjt: result.cd_pjt ?? "", // 요청 라인의 프로젝트
                    });
                    break;
                }
              }

              getGridCurrent("grid2").applyBlock(addedNode);
            }
          }
        });
        getGridCurrent("grid1").applyBlock(focusedRowNodeH);

        if (lotItemList.length > 0) {
          const searchLotData = await fetchLotInv(lotItemList);
          const lotData = searchLotData.map((item: any) => {
            const { no_lot, ...rest } = item; // no_lot만 추출
            return {
              ...rest,
              no_barcode: no_lot, // 필드명 바꾸기
              fg_unit: "LOT", // LOT/SN구분
              qt_isu: 0, // 적용량 추가
            };
          });

          setLotSnData((prevData: any[]) => {
            // 기존에 [품목+창고]가 있는지 확인하여 없는 것만 필터링
            const newData = lotData.filter(
              (newItem: any) =>
                !prevData.some((oldItem: any) => sameInvKey(oldItem, newItem))
            );

            // 추가할 데이터가 있으면 합치고, 없으면 기존 상태 유지
            return newData.length === 0 ? prevData : [...prevData, ...newData];
          });
        }
        if (snItemList.length > 0) {
          const searchSnData = await fetchSnInv(snItemList);
          const snData = searchSnData.map((item: any) => {
            const { no_sn, ...rest } = item;
            return {
              ...rest,
              no_barcode: no_sn, // 필드명 바꾸기
              fg_unit: "SN", // LOT/SN구분
              no_src: item.no_io, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
              no_line_src: item.no_line, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
              qt_isu: 1, // 적용량 추가
              no_origin: item.no_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
              no_line_origin: item.no_line_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
            };
          });

          setLotSnData((prevData: any[]) => {
            // 기존에 [품목+창고]가 있는지 확인하여 없는 것만 필터링
            const newData = snData.filter(
              (newItem: any) =>
                !prevData.some((oldItem: any) => sameInvKey(oldItem, newItem))
            );

            // 추가할 데이터가 있으면 합치고, 없으면 기존 상태 유지
            return newData.length === 0 ? prevData : [...prevData, ...newData];
          });
        }
        break;
    }
  };

  const onGrid2BtnsClick = async (id: string) => {
    if (id == "addRow") {
      onAddRow();
    }
    if (id == "deleteRow") {
      onDeleteRow();
    }
  };

  const onGrid4BtnsClick = async (id: string) => {
    const focusedRowNode = getGridCurrent("grid2").focusedRowNode();
    if (focusedRowNode == null) {
      await showDialog("alter", "선택된 행이 없습니다.");
      return false;
    }

    const insertRows = getGridCurrent("grid2").getChange().insert ?? [];
    const isInsertRow = insertRows.some(
      (row: any) =>
        row.no_line === focusedRowNode.data.no_line &&
        row.no_io === focusedRowNode.data.no_io
    );
    if (!isInsertRow) {
      await showDialog("alter", "신규 행에서만 입력 가능합니다.");
      return false;
    }

    if (id == "addRow") {
      await selectInv("C", "");
    }

    if (id == "deleteRow") {
      if (getGridCurrent("grid4").getDisplayedRowCount() < 1) {
        return;
      }

      const focusedNodeInfo = getGridCurrent("grid4").focusedRowNode();

      getGridCurrent("grid3").forEachNode((node: RowNode) => {
        if (
          sameInvKey(node.data, focusedNodeInfo.data) &&
          node.data.no_barcode === focusedNodeInfo.data?.no_barcode &&
          node.data.dt_io === focusedNodeInfo.data?.dt_io_origin
        ) {
          node.setDataValue(
            "qt_inv",
            node.data.qt_inv + focusedNodeInfo.data?.qt_good
          );

          if (node.data.fg_unit === "SN") {
            node.setDataValue("qt_isu", 1);
          }

          focusedRowNode.setDataValue(
            "qt_good",
            focusedRowNode.data?.qt_good - focusedNodeInfo.data?.qt_good
          );
          return;
        }
      });
      getGridCurrent("grid4").deleteSelectedRows();
      getGridCurrent("grid3").applyFilters({
        cd_item: focusedRowNode.data.cd_item,
        cd_wh: focusedRowNode.data.cd_wh,
        ...pjtFilter(focusedRowNode.data.cd_pjt),
        qt_inv: "notZero",
      });
    }
  };

  //#region onCellFocused4
  const onCellFocused4 = (): any => {
    txtBarcodeRef.current.focus();
  };
  //#endregion

  const onFocusedRowChangedH = (event: any) => {
    const selectedRow = event; // 클릭한 행 데이터
    if (selectedRow) {
      const no_io = selectedRow.no_io;
      getGridCurrent("grid2").selectAndFocusRow(0);
      setSelectedDocu(no_io);

      const rowCount = getGridCurrent("grid2").getDisplayedRowCount();

      if (rowCount > 0) {
        getGridCurrent("grid2").clearFocus();
        getGridCurrent("grid2").selectAndFocusRow(0);
      } else {
        getGridCurrent("grid4").applyFilters({
          no_io: "",
          no_line: "",
        });
      }
    } else {
      return;
    }
  };

  //#region onFocusedRowChangedL
  const onFocusedRowChangedL = (event: any) => {
    const selectedRow = event; // 클릭한 행 데이터
    if (selectedRow) {
      const insertRows = getGridCurrent("grid2").getChange().insert ?? [];
      const isInsertRow = insertRows.some(
        (row: any) =>
          row.no_line === selectedRow.no_line && row.no_io === selectedRow.no_io
      );
      if (isInsertRow) {
        getGridCurrent("grid3").applyFilters({
          cd_item: selectedRow.cd_item,
          cd_wh: selectedRow.cd_wh,
          ...pjtFilter(selectedRow.cd_pjt),
          qt_inv: "notZero",
        });
      } else {
        getGridCurrent("grid3").applyFilters({
          cd_item: "",
          cd_wh: "",
          ...pjtFilter(""),
        });
      }
    } else {
      getGridCurrent("grid3").applyFilters({
        cd_item: "",
        cd_wh: "",
        ...pjtFilter(""),
      });
    }
  };
  //#endregion

  const onCellValueChangedH = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;
    const rowCount = getGridCurrent("grid2").getDisplayedRowCount();

    switch (colId) {
      case "cd_io":
        const ioType = iotypeRef.current.find(
          (item) => item.cd_io === rowData.cd_io
        );

        rowNode.setDataValue("fg_io", ioType.fg_io);
        rowNode.setDataValue("fg_money", ioType.fg_money);
        rowNode.setDataValue("fg_map", ioType.fg_map);

        if (rowCount > 0) {
          gridRefs.current["grid2"].current?.forEachFilteredNode(
            (node: RowNode) => {
              node.setDataValue("cd_io", rowData.cd_io);
              node.setDataValue("fg_io", ioType.fg_io);
              node.setDataValue("fg_money", ioType.fg_money);
              node.setDataValue("fg_map", ioType.fg_map);
            }
          );
        }
        break;
      case "cd_cust":
        rowNode.setDataValue("cd_wh_ref", rowData.cd_cust);
        rowNode.setDataValue("cd_cust_dlv", rowData.cd_cust);
        getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
          node.setDataValue("cd_wh_ref", rowData.cd_wh_ref);
        });
        break;
      case "dt_io":
        getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
          node.setDataValue("dt_io", rowData.dt_io);
        });
        getGridCurrent("grid4").forEachNode((node: RowNode) => {
          if (node.data.no_io === rowData.no_io) {
            node.setDataValue("dt_io", rowData.dt_io);
          }
        });
        break;
      case "cd_wh":
        getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
          node.setDataValue("cd_wh", rowData.cd_wh);
        });
        break;
    }
  };

  const onCellValueChangedL = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    // 품목(또는 프로젝트) 바뀌면 수량 초기화
    switch (colId) {
      case "cd_item":
      case "cd_pjt":
        rowData.qt = 0;
        rowData.qt_good = 0;
        rowData.qt_bad = 0;
        break;
    }

    const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
    switch (colId) {
      case "cd_item":
      case "cd_pjt": // 프로젝트가 라인에 있어 프로젝트 변경도 재고 재조회
        if (colId === "cd_pjt" && mas0002 !== "Y") break;
        if (
          rowData.cd_item !== "" &&
          rowData.cd_item !== null &&
          rowData.cd_item !== undefined &&
          focusedRowNodeH.data.cd_wh !== "" &&
          focusedRowNodeH.data.cd_wh !== null &&
          focusedRowNodeH.data.cd_wh !== undefined &&
          rowData.no_req === undefined &&
          // 프로젝트 사용(MAS0002=Y) 시에는 라인의 프로젝트까지 있어야 재고를 불러온다
          (mas0002 !== "Y" || converter.getString(rowData.cd_pjt) !== "")
        ) {
          // 적용건은 여기서 돌지 않음
          // 품목이나 창고가 바뀌면 꼬일 수 있으므로 초기화
          rowData.qt = 0;
          rowData.qt_good = 0;
          rowData.qt_bad = 0;
          resetRowData();

          const data = {
            cd_biz: rowData.cd_biz,
            cd_item: rowData.cd_item,
            cd_wh: focusedRowNodeH.data.cd_wh,
            cd_loc: "",
            cd_pjt: rowData.cd_pjt ?? "",
          };

          if (!lotSnDataRef.current.some((item) => sameInvKey(item, data))) {
            switch (rowData.fg_unit) {
              case "LOT":
                const searchLotData = await fetchLotInv(data);
                const lotData = searchLotData.map((item: any) => {
                  const { no_lot, ...rest } = item; // no_lot만 추출

                  return {
                    ...rest,
                    no_barcode: no_lot, // 필드명 바꾸기
                    fg_unit: "LOT", // LOT/SN구분
                    qt_isu: 0, // 적용량 추가
                  };
                });

                // 이전 데이터에 새로 조회한 데이터 합치기
                setLotSnData((prevData) => {
                  // 이미 해당 품목+창고 데이터가 prevData에 있는지 다시 한번 확인 (상태 업데이트 시점의 최신값 기준)
                  const isAlreadyExist = prevData.some((item) =>
                    sameInvKey(item, data)
                  );

                  if (isAlreadyExist) return prevData; // 있으면 추가 안 함
                  return [...prevData, ...lotData];
                });
                break;
              case "SN":
                const searchSnData = await fetchSnInv(data);
                const snData = searchSnData.map((item: any) => {
                  const { no_sn, ...rest } = item;

                  return {
                    ...rest,
                    no_barcode: no_sn, // 필드명 바꾸기
                    fg_unit: "SN", // LOT/SN구분
                    no_src: item.no_io, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
                    no_line_src: item.no_line, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
                    qt_isu: 1, // 적용량 추가
                    no_origin: item.no_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
                    no_line_origin: item.no_line_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
                  };
                });

                // 이전 데이터에 새로 조회한 데이터 합치기
                setLotSnData((prevData) => {
                  // 이미 해당 품목+창고 데이터가 prevData에 있는지 다시 한번 확인 (상태 업데이트 시점의 최신값 기준)
                  const isAlreadyExist = prevData.some((item) =>
                    sameInvKey(item, data)
                  );

                  if (isAlreadyExist) return prevData; // 있으면 추가 안 함
                  return [...prevData, ...snData];
                });
                break;
            }
          }
          getGridCurrent("grid3").applyFilters({
            cd_item: rowData.cd_item,
            cd_wh: focusedRowNodeH.data.cd_wh,
            ...pjtFilter(rowData.cd_pjt),
            qt_inv: "notZero",
          });
        } else {
          resetRowData();
        }
        break;
    }

    if (colId === "dt_io") {
      const rowCount = getGridCurrent("grid2").getDisplayedRowCount();
      if (rowCount === 1) return;
      if (!rowData.colId) return;
    }

    if (colId === "qt_good" || colId === "qt_bad") {
      rowNode.setDataValue("qt", rowData.qt_good + rowData.qt_bad);
    }
  };

  const onCellValueChangedLotSn = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    if (colId === "qt_isu") {
      if (rowData.qt_inv < rowData.qt_isu) {
        await showDialog("alter", "재고량을 초과했습니다.");
        rowNode.setDataValue("qt_isu", 0);
      } else {
        if (rowData.qt_isu === 0) {
          getGridCurrent("grid3").setCheckedFocusedRow(false, "noEvent");
        } else {
          getGridCurrent("grid3").setCheckedFocusedRow(true, "noEvent");
        }
      }
    }
  };

  const onBarcodeEnter = async (e: any) => {
    if (getGridCurrent("grid2").getDisplayedRowCount() < 1) return;

    const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
    const focusedRowNodeL = getGridCurrent("grid2").focusedRowNode();

    getGridCurrent("grid3").applyFilters({
      cd_item: focusedRowNodeL.data.cd_item,
      cd_wh: focusedRowNodeH.data.cd_wh,
      ...pjtFilter(focusedRowNodeL.data.cd_pjt),
      qt_inv: "notZero",
    });

    const barcode = e;
    if (barcode === "" || barcode === null || barcode === undefined) return;

    // 스캔로직
    if (!switchRef.current?.getChecked()) {
      await selectInv("S", barcode);
    } else {
      getGridCurrent("grid3").applyLikeFilters(
        {
          cd_item: focusedRowNodeL.data.cd_item,
          cd_wh: focusedRowNodeH.data.cd_wh,
          ...pjtFilter(focusedRowNodeL.data.cd_pjt),
          qt_inv: "notZero",
          no_barcode: barcode,
          no_ct: barcode,
          no_plt: barcode,
        },
        ["no_barcode", "no_ct", "no_plt"]
      );
    }
  };

  //#region selectInv
  const selectInv = async (id: string, barcode: string = "") => {
    const focusedRowNodeL = getGridCurrent("grid2").focusedRowNode();

    if (focusedRowNodeL == null) {
      await showDialog("alter", "선택된 행이 없습니다.");
      return false;
    }

    if (focusedRowNodeL.data.state !== "insert") {
      await showDialog("alter", "신규 행에서만 수정 가능합니다.");
      return false;
    }

    // 요청 건(qt_req > 0)이면 출고정보 합계가 요청수량을 넘지 않도록 검증
    const qtReq = focusedRowNodeL.data.qt_req;
    const qtFooter = getGridCurrent("grid4").getFooterData().qt_good;
    const isOverReq = (qt: number) => qtReq !== 0 && qtReq < qt + qtFooter;

    let selectedMap = new Map<string, number>(); // 재고선택 키/수량 Map
    let qtAdd = 0,
      qtRemain = 0;

    switch (id) {
      case "F": // 선입선출
        if (numberBoxFifoRef.current.getValue == 0) {
          await showDialog("alter", "수량이 입력되지 않았습니다.");
          return;
        }
        qtRemain = Number(numberBoxFifoRef.current.getValue);

        if (isOverReq(qtRemain)) {
          await showDialog("alter", "출고수량이 요청수량보다 많습니다.");
          return;
        }
        break;
      case "C": // 재고선택
        getGridCurrent("grid3").stopEditing();
        const selectedLotSn = getGridCurrent("grid3").getCheckedRows();
        if (selectedLotSn.length < 1) {
          await showDialog("alter", "선택된 재고가 없습니다.");
          return false;
        }

        let qtChecked = 0;
        selectedLotSn.forEach((row) => {
          const key = `${row.no_barcode}_${row.no_ct}_${row.no_plt}_${row.dt_io}`;
          // 사용자가 그리드3에 입력한 '출고예정수량(qt_isu)'을 가져옴
          const qtIsu = Number(row.qt_isu) || 0;
          selectedMap.set(key, qtIsu);
          qtChecked += qtIsu;
        });

        if (isOverReq(qtChecked)) {
          await showDialog("alter", "출고수량이 요청수량보다 많습니다.");
          return;
        }
        break;
      case "S": // 스캔
        // 요청량 있으면 요청량이 잔량
        if (focusedRowNodeL.data.qt_req !== 0) {
          qtRemain = focusedRowNodeL.data.qt_req - focusedRowNodeL.data.qt;

          if (qtRemain === 0) {
            await showDialog("alter", "요청수량이 다 등록되었습니다.");
            return false;
          }
        }
        // 요청량이 없으면 바코드 전체 선택
        else {
          qtRemain = -1;
        }
        break;
    }

    const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
    const cdWh = focusedRowNodeH?.data?.cd_wh ?? focusedRowNodeL.data?.cd_wh;

    getGridCurrent("grid3").forEachFilteredNode((node: RowNode) => {
      const row = node.data;
      let qtIsu = 0;

      switch (id) {
        case "F": // 선입선출
          if (qtRemain <= 0) return;

          qtIsu = Math.min(row.qt_inv, qtRemain); // 재고량이 적으면 재고량 아니면 출고잔량
          break;
        case "C": // 선택
          const key = `${row.no_barcode}_${row.no_ct}_${row.no_plt}_${row.dt_io}`;
          if (!selectedMap.has(key)) return;

          qtIsu = selectedMap.get(key) || 0; // 선택량 가져옴
          break;
        case "S": // 스캔
          // qtRemain === -1은 요청량 없는 건임 바코드찍은만큼 전체 출고
          if (
            String(row.no_barcode ?? "").toLowerCase() ===
              barcode.toLowerCase() ||
            String(row.no_ct ?? "").toLowerCase() === barcode.toLowerCase() ||
            String(row.no_plt ?? "").toLowerCase() === barcode.toLowerCase()
          ) {
            if (qtRemain !== -1) {
              if (qtRemain <= 0) return;

              qtIsu = Math.min(row.qt_inv, qtRemain); // 재고량이 적으면 재고량 아니면 출고잔량
            } else {
              qtIsu = row.qt_inv;
            }
          }
          break;
      }

      if (qtIsu <= 0) return;

      const newRow = {
        cd_firm: focusedRowNodeL.data?.cd_firm,
        cd_biz: focusedRowNodeL.data?.cd_biz,
        no_io: focusedRowNodeL.data?.no_io,
        no_line: focusedRowNodeL.data?.no_line,
        dt_io: focusedRowNodeL.data?.dt_io,
        tp_slip: focusedRowNodeL.data?.tp_slip,
        fg_slip: focusedRowNodeL.data?.fg_slip,
        fg_io: focusedRowNodeL.data?.fg_io,
        fg_money: focusedRowNodeL.data?.fg_money,
        cd_io: focusedRowNodeL.data?.cd_io,
        cd_item: focusedRowNodeL.data?.cd_item,
        cd_wh: cdWh, // 이동정보(grid1) 출고창고
        no_barcode: node.data.no_barcode,
        fg_unit: node.data.fg_unit,
        qt_good: qtIsu,
        no_ct: node.data.no_ct,
        no_plt: node.data.no_plt,
        no_src: node.data.no_src, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
        no_line_src: node.data.no_line_src, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
        dt_io_origin: node.data.dt_io, // 원 LOT 입고일자
        no_origin: node.data.no_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
        no_line_origin: node.data.no_line_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
        cd_pjt: focusedRowNodeL.data?.cd_pjt ?? "", // 라인 프로젝트(도착 프로젝트 없음)
      };

      // qtRemain === -1은 요청량 없는 건
      if (qtRemain !== -1) {
        qtRemain -= qtIsu; // 잔량 빼주고
      }
      qtAdd += qtIsu; // 총 출고량 더하고

      node.setDataValue("qt_isu", 0);
      node.setDataValue("qt_inv", node.data.qt_inv - qtIsu);

      let isExist = false;
      // 기존항목에 있으면 수량만 업데이트
      getGridCurrent("grid4").forEachFilteredNode((node4: RowNode) => {
        if (
          sameInvKey(node4.data, { ...focusedRowNodeL.data, cd_wh: cdWh }) &&
          node4.data.no_barcode === node.data.no_barcode &&
          node4.data.dt_io_origin === node.data.dt_io &&
          node4.data.no_ct === node.data.no_ct &&
          node4.data.no_plt === node.data.no_plt
        ) {
          node4.setDataValue("qt_good", node4.data.qt_good + qtIsu);
          isExist = true;
          return;
        }
      });
      if (!isExist) {
        getGridCurrent("grid4").addRow(newRow);
      }
    });

    focusedRowNodeL.setDataValue(
      "qt_good",
      focusedRowNodeL.data?.qt_good + qtAdd
    );
    numberBoxFifoRef.current.setValue(0);

    getGridCurrent("grid3").clearCheckBox();
    getGridCurrent("grid3").refreshFooter();
    getGridCurrent("grid4").refreshFooter();
    getGridCurrent("grid3").applyFilters({
      cd_item: focusedRowNodeL.data.cd_item,
      cd_wh: cdWh,
      ...pjtFilter(focusedRowNodeL.data.cd_pjt),
      qt_inv: "notZero",
    });

    if (id === "S") {
      if (qtAdd === 0) {
        await showDialog("alter", "바코드 정보를 찾을 수 없습니다.");
      }
    }
  };
  //#endregion

  const resetRowData = (): any => {
    if (getGridCurrent("grid4").getDisplayedRowCount() < 1) return;

    getGridCurrent("grid4").forEachFilteredNode((node4: RowNode) => {
      getGridCurrent("grid3").forEachNode((node: RowNode) => {
        if (
          sameInvKey(node4.data, node.data) &&
          node4.data.no_barcode === node.data?.no_barcode
        ) {
          node.setDataValue("qt_inv", node.data.qt_inv + node4.data.qt_good);

          if (node.data?.fg_unit === "SN") {
            node.setDataValue("qt_isu", 1);
          }
          return;
        }
      });
    });
    getGridCurrent("grid4").deleteAllRows();
  };

  const onActivate = async (nodeData: any) => {
    if (!nodeData) return;
    if ((Menuprops.figure as string) === "fg_drilldown.Search") {
      const selectedData = {
        cd_firm: nodeData.cd_firm,
        cd_biz: nodeData.cd_biz,
        no_io: nodeData.no_io,
        dt_from: nodeData.dt_io,
        dt_to: nodeData.dt_io,
      };
      const searchData = {
        ...formData,
        ...selectedData,
      };
      setFormData(searchData);
      onSearch(searchData);
    }
  };

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onFocusedRowChanged: onFocusedRowChangedH,
          onCellValueChanged: onCellValueChangedH,
          onBeforeAddRow: onBeforeAddRow1,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onCellValueChanged: onCellValueChangedL,
          onFocusedRowChanged: onFocusedRowChangedL,
          onBtnAddClick: onGrid2BtnsClick,
          onBtnDeleteClick: onGrid2BtnsClick,
          onBeforeAddRow: onBeforeAddRow2,
        },
      },
      grid3: {
        columns: columns.grid3,
        dataSource: lotSnData,
        events: {
          onCellValueChanged: onCellValueChangedLotSn,
        },
      },
      grid4: {
        columns: columns.grid4,
        dataSource: dataSources.grid4,
        events: {
          onBtnAddClick: onGrid4BtnsClick,
          onBtnDeleteClick: onGrid4BtnsClick,
          onCellFocused: onCellFocused4,
        },
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="35.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{ rowSelection: "single", title: "이동정보" }}
            events={grids.grid1.events}
            filter={{ refs: [gridRefs.current["grid2"]], columns: ["no_io"] }}
            header={
              <div>
                <SnButton
                  text={"요청적용"}
                  onClick={() => {
                    onGrid1BtnsClick("btn_POP1");
                  }}
                />
              </div>
            }
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="55.5%">
          <div className="flex w-full h-full min-w-0 space-x-1.5">
            <SnResizeBar direction={"width"} id="panel3" orgSize="60%">
              <SnGrid
                ref={gridRefs.current["grid2"]}
                columnConfig={grids.grid2.columns}
                rowData={grids.grid2.dataSource}
                etcProps={{ rowSelection: "multiple", title: "상세정보" }}
                events={grids.grid2.events}
                filter={{
                  refs: [gridRefs.current["grid4"]],
                  columns: ["no_io", "no_line"],
                }}
              />
            </SnResizeBar>
            <div className="flex-1 min-w-0 flex flex-col h-full space-y-1.5">
              <SnResizeBar direction={"height"} id="panel4" orgSize="50%">
                <SnGrid
                  ref={gridRefs.current["grid3"]}
                  columnConfig={grids.grid3.columns}
                  rowData={lotSnData}
                  etcProps={{
                    rowSelection: "single",
                    title: "재고리스트",
                    ischeckboxSelection: true,
                  }}
                  events={grids.grid3.events}
                  header={
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <SnSwitch
                        ref={switchRef}
                        labelCheck="검색"
                        labelUncheck="스캔"
                        defaultChecked={false}
                      />
                      <div>
                        <SnBarcodeBox
                          onEnter={onBarcodeEnter}
                          inputRef={txtBarcodeRef}
                        />
                      </div>
                      <div className="w-[80px]">
                        <SnNumberBox
                          inputRef={numberBoxFifoRef}
                          customFormat="QT"
                        />
                      </div>
                      <div>
                        <SnButton
                          text="선입선출"
                          onClick={() => {
                            selectInv("F", "");
                          }}
                        />
                      </div>
                    </div>
                  }
                />
              </SnResizeBar>
              <div className="flex-1 min-h-0">
                <SnGrid
                  ref={gridRefs.current["grid4"]}
                  columnConfig={grids.grid4.columns}
                  rowData={grids.grid4.dataSource}
                  etcProps={{ rowSelection: "single", title: "출고정보" }}
                  events={grids.grid4.events}
                />
              </div>
            </div>
          </div>
        </SnResizeBar>
        <SnFileUploadBox selectedDocu={selectedDocu} selectedLineDocu={0} />
      </div>
    </div>
  );
});

export default SN_MTL_SCT_REG_003;
